/**
 * ConfirmDialog Component
 *
 * Reusable confirmation dialog for destructive actions. Wraps Modal so it
 * inherits focus trap, Escape handling, body-scroll lock, and overlay click.
 *
 * Pass `requiredText` for high-stakes bulk actions: the confirm button stays
 * disabled until the user types that exact text ("type 6 to delete 6 events").
 */

import { useEffect, useState } from 'react';
import Modal from './Modal';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  variant?: 'danger' | 'warning' | 'default';
  /** When set, confirm is disabled until the user types this exact text. */
  requiredText?: string;
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isLoading = false,
  variant = 'danger',
  requiredText,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // Reset the typed text every time the dialog opens so a previous
  // confirmation can't pre-arm the next one.
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen]);

  const confirmBlocked = requiredText !== undefined && typed.trim() !== requiredText;

  const variantClasses = {
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    default: 'bg-primary-600 hover:bg-primary-700 focus:ring-rack',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" labelledBy="confirm-dialog-title">
      {/* The form makes Enter in the type-to-confirm input submit. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!confirmBlocked && !isLoading) onConfirm();
        }}
      >
        <div className="p-6">
          <h3 id="confirm-dialog-title" className="text-lg font-semibold text-gray-900 mb-2">
            {title}
          </h3>
          <p className={`text-gray-600 ${requiredText !== undefined ? 'mb-4' : 'mb-6'}`}>
            {message}
          </p>
          {requiredText !== undefined && (
            <>
              <label htmlFor="type-confirm-input" className="block text-sm text-gray-600 mb-1">
                Type <span className="font-semibold text-gray-900">{requiredText}</span> to confirm
              </label>
              <input
                id="type-confirm-input"
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={isLoading}
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack mb-6"
              />
            </>
          )}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!confirmBlocked && !isLoading) onConfirm();
              }}
              disabled={isLoading || confirmBlocked}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${variantClasses[variant]}`}
            >
              {isLoading ? 'Loading...' : confirmLabel}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
